/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CustomTaskFormBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the maintenance module.
----------------------------------------------------------------*/


import { FileCode } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Toggle } from '../../../components/ui/Toggle';
import { useI18n } from '../../../hooks/useI18n';
import { CronProfile, Script } from '../api';

const INPUT_CLS = 'w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary';

interface FormData {
    name: string;
    description: string;
    scriptPath: string;
    scriptArgs: string;
    cronProfileId: number | null;
    timeoutSeconds: number;
    runAsUser: string;
    enabled: boolean;
}

interface Props {
    formData: FormData;
    errors: Record<string, string>;
    cronProfiles: CronProfile[];
    scripts: Script[];
    onChange: (field: keyof FormData, value: unknown) => void;
}

export function CustomTaskFormBox({ formData, errors, cronProfiles, scripts, onChange }: Props) {
    const { t } = useI18n();
    const lbl = (text: string) => <label className="text-sm font-medium text-foreground self-center">{text}</label>;

    return (
        <FormBox title={t('customTasks.form.sections.general')} icon={<FileCode className="h-5 w-5" />}>
            {/* Row 1: Task Name | Cron Profile */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
                {lbl(`${t('customTasks.fields.name')} *`)}
                <div>
                    <input type="text" value={formData.name} onChange={(e) => onChange('name', e.target.value)} className={INPUT_CLS} placeholder={t('customTasks.placeholders.name')} />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>
                {lbl(t('customTasks.fields.cronProfile'))}
                <select value={formData.cronProfileId || ''} onChange={(e) => onChange('cronProfileId', e.target.value === '' ? null : parseInt(e.target.value))} className={INPUT_CLS}>
                    <option value="">{t('customTasks.placeholders.selectCronProfile')}</option>
                    {cronProfiles.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.schedule})</option>)}
                </select>
            </div>

            {/* Row 2: Timeout | Run As User */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start mt-4">
                {lbl(t('customTasks.fields.timeoutSeconds'))}
                <input type="number" value={formData.timeoutSeconds} onChange={(e) => onChange('timeoutSeconds', parseInt(e.target.value) || 300)} min={1} max={86400} className={INPUT_CLS} />
                {lbl(t('customTasks.fields.runAsUser'))}
                <input type="text" value={formData.runAsUser} onChange={(e) => onChange('runAsUser', e.target.value)} className={INPUT_CLS} placeholder="root" />
            </div>

            {/* Row 3: Script Path | Script Args */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start mt-4">
                {lbl(`${t('customTasks.fields.scriptPath')} *`)}
                <div>
                    <select value={formData.scriptPath} onChange={(e) => onChange('scriptPath', e.target.value)} className={INPUT_CLS}>
                        <option value="">{t('customTasks.placeholders.selectScript')}</option>
                        {scripts.length === 0 && <option value="" disabled>{t('customTasks.messages.noScripts')}</option>}
                        {scripts.map((s) => <option key={s.path} value={s.path}>{s.name} ({s.path})</option>)}
                    </select>
                    {errors.scriptPath && <p className="text-red-500 text-sm mt-1">{errors.scriptPath}</p>}
                </div>
                {lbl(t('customTasks.fields.scriptArgs'))}
                <input type="text" value={formData.scriptArgs} onChange={(e) => onChange('scriptArgs', e.target.value)} className={INPUT_CLS} placeholder={t('customTasks.placeholders.scriptArgs')} />
            </div>

            {/* Row 4: Enabled */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center mt-4">
                {lbl(t('customTasks.fields.enabled'))}
                <div className="md:col-span-3">
                    <Toggle value={formData.enabled} onChange={(v) => onChange('enabled', v)} />
                </div>
            </div>
        </FormBox>
    );
}
